import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useAnimation } from 'framer-motion';
import YouTube from 'react-youtube';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BookCallModal from '../components/BookCallModal';

const PlaylistShowcase = () => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const [playlistVideos, setPlaylistVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playlistInfo, setPlaylistInfo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isMuted] = useState(false);
  
  // Refs for scroll functionality
  const containerRef = useRef(null);
  const videoRefs = useRef([]);
  const youtubePlayers = useRef({});
  const y = useMotionValue(0);
  const controls = useAnimation();

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // SCROLL TO TOP using useLayoutEffect (runs BEFORE paint)
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Backup scroll to top in useEffect
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Load user data from sessionStorage
  useEffect(() => {
    const savedUserData = sessionStorage.getItem('userData');
    if (savedUserData) {
      setUserData(JSON.parse(savedUserData));
    }
  }, []);


  // YouTube API key - replace with your actual key
  const YOUTUBE_API_KEY = 'AIzaSyDrpIx_Ouc1XFb4bEZO6Bs32GLg9YZxzoM';

  // Playlist data mapping - will be fetched from YouTube API
  const playlistData = {
    'PLv7ad17r_DuB8jqyOW7Q8V4TF65TtWBtl': {
      title: 'Loading...',
      description: 'Loading playlist information...',
      category: 'Loading...'
    },
    'PLv7ad17r_DuAXlcrSWzh39pGNpdpkB8p8': {
      title: 'Loading...',
      description: 'Loading playlist information...',
      category: 'Loading...'
    },
    'PLv7ad17r_DuCgHWG3xRL2sbHZdODmivf0': {
      title: 'Loading...',
      description: 'Loading playlist information...',
      category: 'Loading...'
    },
    'PLv7ad17r_DuBINNL70CMur08-k2JX-upc': {
      title: 'Loading...',
      description: 'Loading playlist information...',
      category: 'Loading...'
    }
  };

  useEffect(() => {
    if (playlistId) {
      setPlaylistInfo(playlistData[playlistId]);
      fetchPlaylistInfo(playlistId);
      fetchPlaylistVideos(playlistId);
    }
  }, [playlistId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlaylistInfo = async (playlistId) => {
    try {
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/playlists?part=snippet&id=${playlistId}&key=${YOUTUBE_API_KEY}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.items && data.items.length > 0) {
          const playlist = data.items[0];
          setPlaylistInfo({
            title: playlist.snippet.title,
            description: playlist.snippet.description,
            category: playlist.snippet.title // Use title as category for now
          });
        }
      }
    } catch (err) {
      console.error('Error fetching playlist info:', err);
    }
  };

  const fetchPlaylistVideos = async (playlistId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Using YouTube Data API v3
      const url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet,contentDetails&playlistId=${playlistId}&maxResults=50&key=${YOUTUBE_API_KEY}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`API Error: ${errorData.error?.message || 'Failed to fetch playlist'}`);
      }
      
      const data = await response.json();
      
      if (data.items && data.items.length > 0) {
        const videos = data.items
          .filter(item => !item.snippet.title.toLowerCase().includes('deleted video'))
          .map(item => ({
            id: item.snippet.resourceId.videoId,
            title: item.snippet.title,
            thumbnail: item.snippet.thumbnails.maxres?.url || item.snippet.thumbnails.high?.url || item.snippet.thumbnails.default?.url,
            duration: 'HD'
          }));
        
        setPlaylistVideos(videos);
      } else {
        setError('No videos found in this playlist');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching playlist:', err);
      setError(`Failed to load playlist: ${err.message}`);
      setLoading(false);
    }
  };

  const animateToVideo = useCallback((index) => {
    const scrollY = -index * window.innerHeight;
    controls.start({
      y: scrollY,
      transition: { duration: 0.3, ease: "easeOut" }
    });
  }, [controls]);

  const handleVideoSelect = (video, index) => {
    setSelectedVideo(video);
    setCurrentVideoIndex(index);
    // Scroll to the clicked video's position
    const scrollY = -index * window.innerHeight;
    y.set(scrollY);
    animateToVideo(index);
  };

  const handleNextVideo = useCallback(() => {
    if (currentVideoIndex < playlistVideos.length - 1) {
      const nextIndex = currentVideoIndex + 1;
      setCurrentVideoIndex(nextIndex);
      setSelectedVideo(playlistVideos[nextIndex]);
      // Animate scroll to next video
      animateToVideo(nextIndex);
    }
  }, [currentVideoIndex, playlistVideos, animateToVideo]);

  const handlePrevVideo = useCallback(() => {
    if (currentVideoIndex > 0) {
      const prevIndex = currentVideoIndex - 1;
      setCurrentVideoIndex(prevIndex);
      setSelectedVideo(playlistVideos[prevIndex]);
      // Animate scroll to previous video
      animateToVideo(prevIndex);
    }
  }, [currentVideoIndex, playlistVideos, animateToVideo]);

  const closeVideoPlayer = () => {
    setSelectedVideo(null);
    setCurrentVideoIndex(0);
    y.set(0);
  };

  // Handle scroll events
  const handleDragEnd = (event, info) => {
    const threshold = 80;
    const velocity = info.velocity.y;
    const offset = info.offset.y;

    // More sensitive scrolling for better reel experience
    if (Math.abs(velocity) > 300 || Math.abs(offset) > threshold) {
      if (velocity > 0 || offset > threshold) {
        // Scroll down - previous video
        if (currentVideoIndex > 0) {
          handlePrevVideo();
        } else {
          // Snap back to first video
          animateToVideo(0);
        }
      } else if (velocity < 0 || offset < -threshold) {
        // Scroll up - next video
        if (currentVideoIndex < playlistVideos.length - 1) {
          handleNextVideo();
        } else {
          // Snap back to last video
          animateToVideo(playlistVideos.length - 1);
        }
      }
    } else {
      // Snap back to current position
      animateToVideo(currentVideoIndex);
    }
  };

  // Handle wheel scroll
  const handleWheel = useCallback((event) => {
    if (!selectedVideo) return;
    
    event.preventDefault();
    const delta = event.deltaY;
    
    if (delta > 0 && currentVideoIndex < playlistVideos.length - 1) {
      // Scroll down - next video
      handleNextVideo();
    } else if (delta < 0 && currentVideoIndex > 0) {
      // Scroll up - previous video
      handlePrevVideo();
    }
  }, [selectedVideo, currentVideoIndex, playlistVideos.length, handleNextVideo, handlePrevVideo]);

  // Add wheel event listener when modal is open
  useEffect(() => {
    if (selectedVideo) {
      document.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        document.removeEventListener('wheel', handleWheel);
      };
    }
  }, [selectedVideo, handleWheel]);

  // Intersection Observer for auto-play/pause
  useEffect(() => {
    if (!selectedVideo || !videoRefs.current.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const videoIndex = parseInt(entry.target.dataset.videoIndex);
          const player = youtubePlayers.current[videoIndex];
          
          if (entry.isIntersecting) {
            if (player) {
              player.playVideo();
            }
          } else {
            if (player) {
              player.pauseVideo();
            }
          }
        });
      },
      {
        threshold: 0.7, // Video is considered visible when 70% is in view
        rootMargin: '0px'
      }
    );

    // Add a small delay to ensure refs are ready
    setTimeout(() => {
      videoRefs.current.forEach((ref) => {
        if (ref) observer.observe(ref);
      });
    }, 100);

    return () => {
      observer.disconnect();
    };
  }, [selectedVideo, playlistVideos.length]);


  return (
    <div className="min-h-screen bg-black">
      {/* Same Navigation as Homepage */}
      <Navigation userData={userData} />
      
      {/* Hero Section */}
      <section className="pt-20 pb-10 bg-gradient-to-b from-black to-[#0a0a0a]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          {/* Back Button */}
          <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-8">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
            >
              <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Back</span>
            </motion.button>
          </div>
          
          <div className="flex items-center justify-center gap-3 mb-6">
            {/* <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div> */}
            {/* <span className="text-[#999999] text-sm font-medium">Live Playlist</span> */}
          </div>
          <div className="w-screen bg-white/10 backdrop-blur-sm border-y border-white/20 px-8 pt-6 mb-8 -mx-6 lg:-mx-8">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-semibold text-white leading-tight">
                {playlistInfo?.title || 'Playlist Showcase'}
              </h1>
              <p className="text-[#b3b3b3] text-lg md:text-xl leading-relaxed mb-6">
                {playlistInfo?.description}
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Portfolio Section */}
      {/* <section className="pb-16 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              <span className="text-[#999999]">My Latest </span><span className="text-white">Projects</span>
            </h2>
            <p className="text-[#b3b3b3] text-lg max-w-3xl mx-auto leading-relaxed">
              This carefully curated playlist showcases professional video editing and motion graphics work. 
              Each video demonstrates different techniques, styles, and creative approaches to visual storytelling.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-[#0E0E0E] border border-[#333333] rounded-2xl p-6 text-center"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-white text-xl font-semibold mb-3">Fast Turnaround</h3>
              <p className="text-[#b3b3b3] text-sm">Quick delivery without compromising on quality</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-[#0E0E0E] border border-[#333333] rounded-2xl p-6 text-center"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-white text-xl font-semibold mb-3">Professional Quality</h3>
              <p className="text-[#b3b3b3] text-sm">High-end production values and attention to detail</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="bg-[#0E0E0E] border border-[#333333] rounded-2xl p-6 text-center"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-white text-xl font-semibold mb-3">Creative Excellence</h3>
              <p className="text-[#b3b3b3] text-sm">Innovative approaches to visual storytelling</p>
            </motion.div>
          </div>
        </div>
      </section> */}

      {/* Video Grid Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
                {loading ? (
            <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                      <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-6"></div>
                      <h3 className="text-white text-xl font-semibold mb-2">Loading Playlist</h3>
                      <p className="text-white/60">Fetching your videos...</p>
                    </div>
                  </div>
                ) : error ? (
            <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-white text-xl font-semibold mb-2">Failed to Load</h3>
                      <p className="text-white/60 mb-4">{error}</p>
                      <button 
                        onClick={() => window.location.reload()}
                        className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {playlistVideos.map((video, index) => (
                  <motion.div
                    key={video.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  onClick={() => handleVideoSelect(video, index)}
                  className="group cursor-pointer"
                >
                  <div className="relative bg-[#0E0E0E] border border-[#333333] rounded-2xl overflow-hidden hover:bg-[#0f0f0f] transition-all duration-500 hover:border-[#444444] hover:shadow-2xl hover:shadow-black/20">
                    {/* Video Thumbnail */}
                    <div className={`${playlistInfo?.title?.toLowerCase().includes('reel') ? 'aspect-[9/16]' : 'aspect-video'} bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] relative overflow-hidden`}>
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors duration-300">
                          <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                            </div>
                          </div>
                      
                     
                    
                      </div>
                    
                    {/* Video Info */}
                    <div className="p-4">
                      <h3 className="text-white text-sm font-semibold line-clamp-2 leading-tight group-hover:text-white/90 transition-colors">
                          {video.title}
                      </h3>
                      
                     
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
          )}
        </div>
      </section>

      {/* Video Player Modal */}
      {selectedVideo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/95 z-50 overflow-hidden"
        >
          {/* Conditional Player Based on Playlist Type */}
          {playlistInfo?.title?.toLowerCase().includes('reel') ? (
            /* Reel Player - Mobile Style Vertical Scrolling */
            <motion.div
              ref={containerRef}
              className="relative w-full h-full"
              style={{ y }}
              animate={controls}
              drag="y"
              dragConstraints={{ top: -(playlistVideos.length - 1) * window.innerHeight, bottom: 0 }}
              dragElastic={0.1}
              onDragEnd={handleDragEnd}
              dragMomentum={false}
            >
              {/* All Videos Stacked Vertically */}
              {playlistVideos.map((video, index) => (
                <div
                  key={video.id}
                  ref={(el) => {
                    if (el) videoRefs.current[index] = el;
                  }}
                  data-video-index={index}
                  className="absolute w-full h-screen flex items-center justify-center"
                  style={{ top: index * window.innerHeight }}
                >
                  {/* Reel Video Container - Mobile Style */}
                  <div className="relative w-full h-full max-w-md mx-auto">
                    {/* Video Player */}
                    <div className="absolute inset-0 bg-black rounded-2xl overflow-hidden">
                      <YouTube
                        videoId={video.id}
                        opts={{
                          height: '100%',
                          width: '100%',
                          playerVars: {
                            autoplay: 0,
                            modestbranding: 1,
                            rel: 0,
                            showinfo: 0,
                            controls: 1,
                            fs: 1,
                            cc_load_policy: 0,
                            iv_load_policy: 3,
                            autohide: 0,
                            mute: isMuted ? 1 : 0
                          }
                        }}
                        onReady={(event) => {
                          youtubePlayers.current[index] = event.target;
                        }}
                        className="w-full h-full"
                      />
                    </div>
                    
                    {/* Progress Indicator (only show on current video) */}
                    {index === currentVideoIndex && (
                      <div className="absolute top-0 left-0 right-0 h-1 bg-white/20">
                        <motion.div
                          className="h-full bg-white"
                          initial={{ width: "0%" }}
                          animate={{ width: `${((currentVideoIndex + 1) / playlistVideos.length) * 100}%` }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>
          ) : (
            /* Traditional 16:9 Player - Proper Modal */
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Modal Container */}
              <div className="relative bg-black/95 backdrop-blur-sm rounded-2xl border border-white/20 shadow-2xl max-w-6xl w-full">
                {/* Video Player */}
                <div className="relative aspect-video bg-black rounded-t-2xl overflow-hidden">
                  <YouTube
                    videoId={selectedVideo.id}
                    opts={{
                      height: '100%',
                      width: '100%',
                      playerVars: {
                        autoplay: 1,
                        modestbranding: 1,
                        rel: 0,
                        showinfo: 0,
                        controls: 1,
                        fs: 1,
                        cc_load_policy: 0,
                        iv_load_policy: 3,
                        autohide: 0,
                        mute: isMuted ? 1 : 0
                      }
                    }}
                    className="w-full h-full"
                  />
                </div>
                
                {/* Video Info Panel */}
                <div className="p-6">
                  <h3 className="text-white text-xl font-bold leading-tight mb-2">
                    {selectedVideo.title}
                  </h3>
                  <p className="text-white/80 text-sm mb-4">
                    {playlistInfo?.title || 'Playlist'}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-white/60 text-sm">
                      <span>{currentVideoIndex + 1} of {playlistVideos.length}</span>
                      <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                      <span>HD</span>
                    </div>
                    {/* Fullscreen Button */}
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={() => {
                        const videoElement = document.querySelector('iframe');
                        if (videoElement && videoElement.requestFullscreen) {
                          videoElement.requestFullscreen();
                        }
                      }}
                      className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors border border-white/20"
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Back Button */}
          <div className="absolute top-6 left-6 z-10">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={closeVideoPlayer}
              className="w-12 h-12 bg-black/80 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-black/90 transition-colors border border-white/20"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </motion.button>
          </div>

          {/* Reel Scroll Indicator */}
          {playlistInfo?.title?.toLowerCase().includes('reel') && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex flex-col items-center gap-2 z-10">
              {playlistVideos.map((_, index) => (
                <div
                  key={index}
                  className={`w-1 h-8 rounded-full transition-colors duration-300 ${
                    index === currentVideoIndex ? 'bg-white' : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Reel Control Console - Right Side with Up/Down Arrows */}
          {playlistInfo?.title?.toLowerCase().includes('reel') && (
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex flex-col items-center gap-4 z-20">
              {/* Up Arrow - Previous Video */}
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={handlePrevVideo}
                disabled={currentVideoIndex === 0}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  currentVideoIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/20'
                }`}
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </motion.button>
              
              {/* Down Arrow - Next Video */}
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={handleNextVideo}
                disabled={currentVideoIndex === playlistVideos.length - 1}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  currentVideoIndex === playlistVideos.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/20'
                }`}
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </motion.button>
            </div>
          )}

        </motion.div>
      )}

      {/* Call to Action Section */}
      <section className="py-16 relative overflow-hidden" style={{ backgroundImage: 'url(/BG.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        {/* Background Overlay */}
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl mb-6">
              {userData ? (
                <>
                  <span className="text-[#999999]">Hey! {userData.name} </span>
                  <span className="text-white">ready to work together?</span>
                </>
              ) : (
                <>
                  <span className="text-[#999999]">Ready to Work </span>
                  <span className="text-white">Together?</span>
                </>
              )}
            </h2>
            <p className="text-[#b3b3b3] text-lg max-w-2xl mx-auto mb-8">
              {userData ? `let's discuss your next project and bring your vision to life with professional editing and motion graphics.` : "Let's discuss your next project and bring your vision to life with professional editing and motion graphics."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={openModal}
                className="bg-white text-black px-8 py-4 rounded-full font-semibold hover:bg-gray-200 transition-colors"
              >
                Get in Touch
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  navigate('/');
                  // Scroll to portfolio section after navigation
                  setTimeout(() => {
                    document.getElementById('portfolio')?.scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }, 100);
                }}
                className="border border-white/60 text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-colors"
              >
                View More Work
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer userData={userData} />

      {/* Book Call Modal */}
      <BookCallModal isOpen={isModalOpen} onClose={closeModal} userData={userData} />
    </div>
  );
};

export default PlaylistShowcase;